<app-shared-action-bar [displayActionSectionBar]="false"></app-shared-action-bar>

<div class="wizard-page container p-4">
  @if (!hideHeader) {
    <div class="mt-20 mb-4">
      <p class="text-xs uppercase tracking-wide text-grey-700 mb-1">Users</p>
      <h1 class="text-2xl font-semibold leading-tight">@if (editMode) {User Edit} @else {New User Wizard}</h1>
      <p class="text-sm text-grey-800">@if (editMode) {Update details and permissions.} @else {Create a user and assign permissions.}</p>
    </div>
  }

  <div class="wizard-card shadow-sm">
    @if (!tabMode) {
      <div class="stepper mb-4">
        <div class="step" [class.active]="displayStep === 1">1. Details</div>
        <div class="step" [class.active]="displayStep === 2">2. Location</div>
        <div class="step" [class.active]="displayStep === 3">3. Permissions</div>
        <div class="step" [class.active]="displayStep === 4">4. {{ 'audit' | customerTerm:true }}</div>
      </div>
    }

    @if (displayStep === 1) {
      <div class="form-grid">
        @if (reactivationLicenseUnavailable) {
          <div class="notice-box">
            <p>This user cannot be reactivated because there are no available user licences. Please contact your account manager.</p>
          </div>
        }
        @if (checkingLicense) {
          <div class="notice-box">
            <p>Checking user licence availability...</p>
          </div>
        }
        @if (!checkingLicense && noLicense) {
          <div class="notice-box">
            <p>No available user licenses, please contact your account manager.</p>
          </div>
        }
        @if (!checkingLicense && !noLicense) {
          <div class="form-row">
            <label>Username</label>
            <input type="text" class="text-box" [(ngModel)]="newUser.Username" (ngModelChange)="resetFlags()" [disabled]="!!createdUser || editMode" />
            @if (invalidUserName) {
              <p class="text-danger-base">Username is invalid</p>
            }
            @if (isUsernameInUse) {
              <p class="text-danger-base">Username must be unique, please select a different username</p>
            }
          </div>

          <div class="form-row">
            <label>Display Name</label>
            <input type="text" class="text-box" [(ngModel)]="newUser.DisplayName" (ngModelChange)="resetFlags(); scheduleDetailsSave()" [disabled]="readOnlyMode" />
            @if (invalidDisplayName) {
              <p class="text-danger-base">Display Name is not valid</p>
            }
          </div>

          <div class="form-row">
            <label>Email</label>
            <input type="email" class="text-box" [ngClass]="{'error': invalidEmail || emailUsed}" [(ngModel)]="newUser.Email" (ngModelChange)="resetEmailFlags()" [disabled]="readOnlyMode" />
            @if (invalidEmail) {
              <p class="text-danger-base">Email is invalid</p>
            }
            @if (emailUsed) {
              <p class="text-danger-base">Email is already assigned to an active account. Update the email before making any further changes to this user.</p>
            }
          </div>

          @if (editMode) {
            <div class="form-row mt-2">
              <h3 class="text-sm font-semibold text-grey-700 mb-2">Account Status</h3>
              <div class="toggle-row">
                <span>Active</span>
                <app-simple-toggle [(isChecked)]="newUser.Active" (isCheckedChange)="scheduleDetailsSave()" [isDisabled]="readOnlyMode"></app-simple-toggle>
              </div>
            </div>
          }

          <div class="form-row mt-6">
            <h3 class="text-sm font-semibold text-grey-700 mb-2">Notifications</h3>
            <div class="toggle-pairs">
              <span>Daily Reminders</span>
              <app-simple-toggle class="notification-toggle" [(isChecked)]="newUser.DailyReminders" (isCheckedChange)="scheduleDetailsSave()" [isDisabled]="readOnlyMode"></app-simple-toggle>
              <span>Weekly Summary</span>
              <app-simple-toggle class="notification-toggle" [(isChecked)]="newUser.WeeklySummary" (isCheckedChange)="scheduleDetailsSave()" [isDisabled]="readOnlyMode"></app-simple-toggle>
              <span>Daily Recaps</span>
              <app-simple-toggle class="notification-toggle" [(isChecked)]="newUser.SendDailyRecaps" (isCheckedChange)="scheduleDetailsSave()" [isDisabled]="readOnlyMode"></app-simple-toggle>
              <span>Action Emails</span>
              <app-simple-toggle class="notification-toggle" [(isChecked)]="newUser.SendActionEmails" (isCheckedChange)="scheduleDetailsSave()" [isDisabled]="readOnlyMode"></app-simple-toggle>
            </div>
          </div>
          @if (editMode && createdUser) {
            <app-landing-page-preference [userId]="createdUser.UserID" [disabled]="readOnlyMode"></app-landing-page-preference>
          }
        }
      </div>
    }

    @if (displayStep === 2) {
      <div class="form-grid">
        <div class="form-row">
          <h3 class="text-sm font-semibold text-grey-700 mb-2">Select Location</h3>
          @if (readOnlyMode) {
            <p class="text-xs text-grey-700 mb-2">Location editing is not available for this user.</p>
          }
          @if (!topLevel) {
            <button class="text-blue-600 text-[11px] flex items-center gap-1 mt-1" type="button" (click)="resetLocations()">
              <i class="pi pi-level-up no-underline text-[10px]"></i>
              <span class="underline">Back to top level</span>
            </button>
          }
          @if (locationsReady) {
            <div class="location-list">
              @for (l of possibleLocations; track l) {
                <div class="location-row">
                  <div
                    class="location-name"
                    [ngClass]="{'selected': isLocationScopeSelected(l.LocationID), 'hover:hover-for-select': l.HasPermission, 'bg-grey-100 cursor-not-allowed': !l.HasPermission, 'bg-grey-300': l.HasPermission && !isLocationScopeSelected(l.LocationID)}">
                    <app-simple-toggle
                      class="mr-2"
                      [isDisabled]="readOnlyMode || !l.HasPermission"
                      [isChecked]="isLocationScopeSelected(l.LocationID)"
                      (isCheckedChange)="toggleLocationSelection(l, $event)">
                    </app-simple-toggle>
                    <span>{{l.LocationName}}</span>
                    @if (!l.Active) {
                      <span class="ml-2 text-grey-900 font-bold">(Inactive)</span>
                    }
                  </div>
                  @if (locationHasChildren(l)) {
                    <button class="btn-inverse location-child-btn" (click)="buildLocationDDL(l.LocationID)" [disabled]="readOnlyMode">Sub Locations</button>
                  }
                </div>
              }
            </div>
          }
        </div>

        <div class="form-row">
          <div class="toggle-row">
            <span>Include Sub Locations</span>
            <app-simple-toggle [(isChecked)]="includeSubLocations" (isCheckedChange)="updateIncludeSubLocations($event)" [isDisabled]="readOnlyMode"></app-simple-toggle>
          </div>
        </div>

        @if (selectedLocationScopes.length > 0) {
          <div class="form-row locations-added">
            <h3 class="text-sm font-semibold text-grey-700 mb-2">Selected Locations</h3>
            <div class="permissions-table-wrapper locations-added-table">
              <table class="permission-table">
                <thead>
                  <tr>
                    <th class="text-left">Location</th>
                    <th>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  @for (l of selectedLocationScopes; track l.LocationID) {
                    <tr>
                      <td class="location-cell">
                        {{l.LocationName}}
                        @if (l.IncludeSubLocations) {
                          <span class="ml-2 text-xs text-grey-700">(Includes sublocations)</span>
                        }
                      </td>
                      <td class="flex justify-center py-1">
                        @if (!readOnlyMode) {
                          <button class="remove-location-btn" type="button" (click)="removeLocationSelection(l.LocationID)" aria-label="Remove location">
                            <i class="pi pi-times"></i>
                          </button>
                        }
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>
    }

    @if (displayStep === 3) {
      @if (!permissionsReady) {
        <p class="text-grey-700">Loading permission options...</p>
      }
      @if (permissionsReady) {
        <div class="form-grid">
          <div class="form-row">
            <details class="mirror-disclosure">
              <summary class="flex items-center gap-2 cursor-pointer text-sm text-grey-700">
                <i class="pi pi-chevron-right text-xs chevron-closed"></i>
                <i class="pi pi-chevron-down text-xs chevron-open"></i>
                <span>Mirror Existing User</span>
              </summary>
              <div class="mirror-row mt-2">
                <select class="text-box max-w-xs mt-0" [(ngModel)]="mirrorUserId">
                  <option [ngValue]="null">Choose an existing user</option>
                  @for (user of mirrorUsers; track user) {
                    <option [ngValue]="user.UserID">{{ user.DisplayName }}</option>
                  }
                </select>
                <button class="btn-primary" (click)="applyMirrorPermissions()" [disabled]="mirrorUserId == null">Apply</button>
                <button class="btn-inverse" (click)="clearPermissions()">Clear</button>
              </div>
              <div class="divider-line"></div>
            </details>
          </div>

          <div class="form-row">
            <div class="toggle-row advanced-toggle-row">
              <span class="advanced-toggle-label">Advanced options</span>
              <app-toggle-switch [(checked)]="useAdvancedPermissions" (checkedChange)="schedulePermissionsSave()"></app-toggle-switch>
            </div>
          </div>
        </div>

        @if (!useAdvancedPermissions) {
          <div class="permissions-table-wrapper permissions-gap desktop-only">
            <table class="permission-table">
              <thead>
                <tr>
                  <th class="text-left location-header">Location</th>
                  <th>{{ 'audit' | customerTerm }} Save</th>
                  <th>{{ 'audit' | customerTerm }} View</th>
                  <th>Action Complete</th>
                  <th>Action View</th>
                  <th>Admin</th>
                </tr>
              </thead>
              <tbody>
                @for (location of newUserLocations; track location) {
                  <tr>
                    <td
                      class="location-cell"
                      [ngClass]="{'pl-2':(location.LocationLevelSeq) == 1,'pl-4':(location.LocationLevelSeq) == 2,'pl-6':(location.LocationLevelSeq) == 3,'pl-8':(location.LocationLevelSeq) >= 4, 'text-grey-500': nonEditableLocationIds.has(location.LocationID)}">
                      {{location.LocationName}}
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateSimplePermission(location.LocationID,1,$event)" [(isChecked)]="location.Features.SimpleSaveAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateSimplePermission(location.LocationID,2,$event)" [(isChecked)]="location.Features.SimpleViewAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateSimplePermission(location.LocationID,3,$event)" [(isChecked)]="location.Features.SimpleCompleteAction" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateSimplePermission(location.LocationID,4,$event)" [(isChecked)]="location.Features.SimpleViewAction" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateSimplePermission(location.LocationID,5,$event)" [(isChecked)]="location.Features.SimpleAdmin" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="mobile-only permissions-gap">
            @for (location of newUserLocations; track location) {
              <div class="mobile-location-card">
                <div class="mobile-location-header" [ngClass]="{'text-grey-500': nonEditableLocationIds.has(location.LocationID)}">{{location.LocationName}}</div>
                <div class="mobile-permission-grid">
                  <div class="mobile-permission-item">
                    <span>{{ 'audit' | customerTerm }} Save</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateSimplePermission(location.LocationID,1,$event)"
                      [(isChecked)]="location.Features.SimpleSaveAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>{{ 'audit' | customerTerm }} View</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateSimplePermission(location.LocationID,2,$event)"
                      [(isChecked)]="location.Features.SimpleViewAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Action Complete</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateSimplePermission(location.LocationID,3,$event)"
                      [(isChecked)]="location.Features.SimpleCompleteAction" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Action View</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateSimplePermission(location.LocationID,4,$event)"
                      [(isChecked)]="location.Features.SimpleViewAction" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Admin</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateSimplePermission(location.LocationID,5,$event)"
                      [(isChecked)]="location.Features.SimpleAdmin" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                  </div>
                </div>
              </div>
            }
          </div>
        }

        @if (useAdvancedPermissions) {
          <div class="permissions-table-wrapper permissions-gap desktop-only">
            <h3 class="text-lg mb-2">Advanced permissions</h3>
            <table class="permission-table">
              <thead>
                <tr>
                  <th class="text-left location-header">Location</th>
                  <th>{{ 'audit' | customerTerm }} View</th>
                  <th>{{ 'audit' | customerTerm }} Save</th>
                  <th>{{ 'audit' | customerTerm }} Edit</th>
                  <th>{{ 'audit' | customerTerm }} Schedule</th>
                  <th>{{ 'audit' | customerTerm }} Delete</th>
                  <th>Action View</th>
                  <th>Action Complete</th>
                  <th>Action Edit</th>
                  <th>Edit Users</th>
                  <th>Edit Locations</th>
                  <th>Group Mgmt</th>
                </tr>
              </thead>
              <tbody>
                @for (location of newUserLocations; track location) {
                  <tr>
                    <td
                      class="location-cell"
                      [ngClass]="{'pl-2':(location.LocationLevelSeq) == 1,'pl-4':(location.LocationLevelSeq) == 2,'pl-6':(location.LocationLevelSeq) == 3,'pl-8':(location.LocationLevelSeq) >= 4, 'text-grey-500': nonEditableLocationIds.has(location.LocationID)}">
                      {{location.LocationName}}
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'ViewAudit',$event)" [(isChecked)]="location.Features.ViewAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'SaveAudit',$event)" [(isChecked)]="location.Features.SaveAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'EditAudit',$event)" [(isChecked)]="location.Features.EditAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'ScheduleAudit',$event)" [(isChecked)]="location.Features.ScheduleAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'DeleteScheduledAudit',$event)" [(isChecked)]="location.Features.DeleteScheduledAudit" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'ViewAction',$event)" [(isChecked)]="location.Features.ViewAction" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'CompleteAction',$event)" [(isChecked)]="location.Features.CompleteAction" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'EditAction',$event)" [(isChecked)]="location.Features.EditAction" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'EditUsers',$event)" [(isChecked)]="location.Features.EditUsers" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'EditLocations',$event)" [(isChecked)]="location.Features.EditLocations" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                    <td>
                      <div class="flex justify-center py-1">
                        <app-simple-toggle (isCheckedChange)="updateAdvancedPermission(location.LocationID,'GroupManagement',$event)" [(isChecked)]="location.Features.GroupManagement" [isDisabled]="readOnlyMode || nonEditableLocationIds.has(location.LocationID)"></app-simple-toggle>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="mobile-only permissions-gap">
            @if (!mobileSelectedLocation) {
              <div class="mobile-location-list">
                @for (location of newUserLocations; track location) {
                  <div class="mobile-location-row">
                    <span class="mobile-location-name">{{location.LocationName}}</span>
                    <button class="btn-inverse" type="button" (click)="selectMobileLocation(location.LocationID)">Edit</button>
                  </div>
                }
              </div>
            }
            @if (mobileSelectedLocation) {
              <div class="mobile-location-detail">
                <button class="mobile-back-link" type="button" (click)="clearMobileLocationSelection()">
                  <span class="underline">Switch location</span>
                </button>
                <div class="mobile-location-header">{{mobileSelectedLocation.LocationName}}</div>
                <div class="mobile-permission-grid">
                  <div class="mobile-permission-item">
                    <span>{{ 'audit' | customerTerm }} View</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'ViewAudit',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.ViewAudit"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>{{ 'audit' | customerTerm }} Save</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'SaveAudit',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.SaveAudit"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>{{ 'audit' | customerTerm }} Edit</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'EditAudit',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.EditAudit"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>{{ 'audit' | customerTerm }} Schedule</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'ScheduleAudit',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.ScheduleAudit"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>{{ 'audit' | customerTerm }} Delete</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'DeleteScheduledAudit',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.DeleteScheduledAudit"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Action View</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'ViewAction',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.ViewAction"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Action Complete</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'CompleteAction',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.CompleteAction"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Action Edit</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'EditAction',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.EditAction"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Edit Users</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'EditUsers',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.EditUsers"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Edit Locations</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'EditLocations',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.EditLocations"></app-simple-toggle>
                  </div>
                  <div class="mobile-permission-item">
                    <span>Group Mgmt</span>
                    <app-simple-toggle
                      (isCheckedChange)="updateAdvancedPermission(mobileSelectedLocation.LocationID,'GroupManagement',$event)"
                      [(isChecked)]="mobileSelectedLocation.Features.GroupManagement"></app-simple-toggle>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      }
    }

    @if (displayStep === 4) {
      @if (auditLoading) {
        <p class="text-grey-700">Loading audits...</p>
      }
      @if (!auditLoading && auditLoaded) {
        <div class="form-row">
          <div class="toggle-row audit-type-toggle-row">
            <span class="text-sm text-grey-700 audit-type-toggle-label">Select by {{ 'audit' | customerTerm }} type</span>
            <app-toggle-switch [(checked)]="useAuditTypeSelection"></app-toggle-switch>
          </div>
        </div>
        @if (useAuditTypeSelection) {
          <div class="permissions-table-wrapper permissions-gap">
            <table class="permission-table">
              <thead>
                <tr>
                  <th class="text-left location-header">{{ 'audit' | customerTerm }} type</th>
                  <th>Access</th>
                </tr>
              </thead>
              <tbody>
                @for (type of auditTypes; track type) {
                  <tr>
                    <td class="location-cell">{{type.AuditType}}</td>
                  <td class="flex justify-center py-2">
                    <app-simple-toggle
                      [isChecked]="isAuditTypeSelected(type.AuditTypeID)"
                      [isDisabled]="readOnlyMode"
                      (isCheckedChange)="toggleAuditType(type.AuditTypeID, $event)"></app-simple-toggle>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        }
        @if (!useAuditTypeSelection) {
          <div class="permissions-table-wrapper permissions-gap">
            <table class="permission-table">
              <tbody>
                <tr>
                  <td class="location-cell font-bold">All</td>
                  <td class="flex justify-center py-2">
                    <app-simple-toggle [(isChecked)]="allAuditPermissions" [isDisabled]="readOnlyMode"
                      (isCheckedChange)="updateAllAuditPermissions()"></app-simple-toggle>
                  </td>
                </tr>
                @for (audit of userAudits.AuditPermissions; track audit) {
                  <tr>
                    <td class="location-cell">{{audit.AuditName}}</td>
                    <td class="flex justify-center py-2">
                      <app-simple-toggle [(isChecked)]="audit.HasPermission" [isDisabled]="readOnlyMode"
                        (isCheckedChange)="checkAllAuditPermissions()"></app-simple-toggle>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }
    }

    @if (!tabMode) {
      <div class="step-actions">
        @if (currentStep !== 1) {
          <button class="btn-inverse" (click)="prevStep()">Back</button>
        }
        <div class="grow"></div>
        @if (currentStep < 4) {
          <button class="btn-primary" (click)="nextStep()" [disabled]="(!readOnlyMode && (checkingLicense || noLicense || isSavingUser || isSavingLocations || isSavingPermissions || (currentStep === 2 && !hasAnyLocationSelection()) || (currentStep === 3 && (!permissionsReady || !hasAnyPermissionSelected()))))">
            @if (readOnlyMode) {<span>Next</span>}
            @if (!readOnlyMode && currentStep === 1) {<span>@if (isSavingUser) {Saving...} @else {Save & Next}</span>}
            @if (!readOnlyMode && currentStep === 2) {<span>@if (isSavingLocations) {Saving...} @else {Save & Next}</span>}
            @if (!readOnlyMode && currentStep === 3) {<span>@if (isSavingPermissions) {Saving...} @else {Save & Next}</span>}
          </button>
        }
        @if (currentStep === 4) {
          @if (readOnlyMode) {
            <button class="btn-primary" (click)="saveWizard()">Exit</button>
          }
          @if (!readOnlyMode) {
            <button class="btn-primary" (click)="saveWizard()" [disabled]="isSavingPermissions">Save & Exit</button>
          }
        }
      </div>
    }
  </div>
</div>
